import { Controller } from "stimulus";
import fetchNoCache from "../functions/fetchNoCache.js";
import fetchUrl from "../functions/fetchUrl.js";
import cleanHtml from "../functions/cleanHtml.js";
import { removeAjaxPending } from "../functions/ajaxPending.js";
import displayFlashMessage from "../functions/displayFlashMessage.js";
import parseJson from "../functions/parseJson.js";

export default class extends Controller {
  static get targets() {
    return ["contentArea"];
  }

  connect() {
    if (!this.preventInitialLoad) {
      this.load();
    }
  }

  // An event's explicit query string (empty included) is used as given. Without one, a container
  // with data-remember-query reloads with the query it last loaded, so a refresh keeps its filter,
  // sort and page, unless data-url has changed since: that query belonged to another URL. Any other
  // container reloads with no query. The first load uses the initial query.
  _fetchQueryString(event) {
    const detail = event && event.detail;

    if (detail && detail.queryString !== undefined) {
      return detail.queryString;
    }

    if (event && this.element.dataset.rememberQuery !== "true") {
      return "";
    }

    if (this.element.dataset.queryUrl !== this.element.dataset.url) {
      return "";
    }

    return this.element.dataset.queryString;
  }

  // Kept on the element, which outlives every load, rather than on the controller.
  _rememberQuery(url, queryString) {
    this.element.dataset.queryUrl = url;
    this.element.dataset.queryString = queryString;
  }

  load(event) {
    const silent = this._isSilent(event);

    if (!silent) {
      this.showOverlay();
    }

    const element = this.element;
    const baseUrl = this.element.dataset.url;
    const queryString = this._fetchQueryString(event);
    const url = fetchUrl(baseUrl, queryString);

    fetchNoCache(url, {}, { silent })
      .then(async ({ response, problem }) => {
        if (!response.ok) {
          // check if response is json that contains json messages
          // i.e. validation error messages through search GET requests
          const contentType = response.headers.get("content-type");
          if (contentType && contentType.indexOf("application/json") !== -1) {
            const data = parseJson(await response.text(), problem);
            if (data) {
              data.messages.forEach(function (message) {
                displayFlashMessage(message);
              });
            }
            throw Error(response.statusText);
          }
        }

        if (response.headers.get("REQUIRES_AUTH") === "1") {
          window.location.replace(this.element.dataset.redirectUrl);
        } else {
          return response.text();
        }
      })
      .then((html) => {
        const element = this.contentAreaTarget;
        element.innerHTML = cleanHtml(html);
        this._rememberQuery(baseUrl, queryString);
        removeAjaxPending(element);
        if (!silent) {
          this.hideOverlay();
        }
      })
      .catch((error) => {
        console.error(error);
        removeAjaxPending(element);
        if (!silent) {
          this.hideOverlay();
        }
      });
  }

  _isSilent(event) {
    return !!(event && event.detail && event.detail.silent);
  }

  clear() {
    this.contentAreaTarget.innerHTML = "";
  }

  showOverlay() {
    const event = new CustomEvent("overlay:show", { bubbles: true });
    this.element.dispatchEvent(event);
  }

  hideOverlay() {
    const event = new CustomEvent("overlay:hide", { bubbles: true });
    this.element.dispatchEvent(event);
  }

  get preventInitialLoad() {
    return this.element.dataset.preventInitialLoad === "true";
  }
}
